import classNames from 'classnames';
import { ReactEventHandler, useCallback, useState } from 'react';
import { Button, Flex, Heading } from 'yoso';
import { ContentModel } from '~/components/Content/Content.hooks';
import { T } from '~/components/I18n';
import { Level as LevelIcon } from '~/components/Icon/Level';
import { Level } from '~/components/Level';
import { levelMetaMap, useCheckInReward } from '~/components/Level/Level.hooks';
import { LevelUpToken, useLevelUpStatus } from '~/components/LevelUp/LevelUp.hooks';
import cube from '~/components/LevelUp/Popup/Result/cube.gif';
import { styles as bottomSheetStyles } from '~/components/Modal/BottomSheet/BottomSheet';
import { NftContent } from '~/components/Nft/Content/NftContent';
import { formatDon } from '~/components/Util/number';
import styles from './LevelUpResult.module.scss';

export type LevelUpResultProps = LevelUpToken & {
  className?: string;
};

export const LevelUpResult = ({ id, className }: LevelUpResultProps) => {
  const [loaded, setLoaded] = useState(false);
  const { data } = useLevelUpStatus(id, { refetchInterval: false });
  const { level, ...content } = data;
  const { data: checkInRewards } = useCheckInReward(level);
  const onImageLoad: ReactEventHandler<HTMLImageElement> = useCallback((e) => {
    setLoaded((loaded) => {
      if (loaded) return loaded;
      const target = e.target as HTMLImageElement;
      // Force reanimate
      // eslint-disable-next-line no-self-assign
      target.src = target.src;
      return true;
    });
  }, []);
  const levelMeta = levelMetaMap[level];
  const rewardFrom = checkInRewards[0]?.amount;

  return (
    <div
      className={classNames(
        styles.root,
        {
          [styles.animate]: loaded,
        },
        className
      )}
      // data-event="impression"
      // data-event-category="levelup_successful_popup"
      // data-event-impression-id="{category}"
    >
      <div className={styles.background}>
        <Heading level={4} className={classNames(styles.title, bottomSheetStyles.heading)}>
          <T name="citizen_membership_levelup_unpacking_popup_title" />
        </Heading>
        <img src={cube} className={styles.image} alt="Now Unpacking..." onLoad={onImageLoad} />
      </div>
      <div className={styles.foreground}>
        <Heading level={5} className={styles.title}>
          <T name="citizen_membership_levelup_done_popup_title" />
        </Heading>
        <p className={styles.description}>
          <T name="citizen_membership_levelup_done_popup_desc1" options={[formatDon(rewardFrom)]} />
        </p>
        <NftContent {...(content as ContentModel)} className={styles.content} />
        <Flex container className={styles.bonus} justifyContent="center">
          <span className={styles.bonusBadge}>
            <LevelIcon className={styles.levelIcon} /> <Level level={level} color={false} />
          </span>
        </Flex>
        <p className={styles.name}>{levelMeta.title}</p>
        <Button
          as="a"
          color="primary"
          className={classNames(styles.button, bottomSheetStyles.confirmButton)}
          href={`${import.meta.env.VITE_CITIZEN_STORE_URL}/nfts/${data?.nftId}`}
          data-event="click"
          data-event-target="goto_seenft_btn">
          <T name="citizen_button_title_see_nft" />
        </Button>
      </div>
    </div>
  );
};
